import type { Verdict } from '@light2live/shared-types';
import type { TrendChange, TrendMetric } from '@/lib/progress-trend';

/**
 * The one way this app says a number moved (#241).
 *
 * Extracted from the headline block when the grouped metric rows needed the
 * same thing. Two chips would have drifted: one saying "2 points better" and
 * the other "up 2" for the same reading is the class of inconsistency the
 * shared direction table exists to prevent, and it would have been colour that
 * drifted first.
 */

/** Below this a change is noise, and every surface says "same as" instead. */
export const FLAT = 0.05;

/**
 * A fill rather than coloured text, because #94c740 and #f6a350 measure 1.69:1
 * and 1.73:1 as text on the page background — they are fills or they are
 * nothing. As fills carrying #1a1a1a they are 8.70:1 and 8.51:1.
 *
 * A verdict of 'flat' or 'none' gets no fill: there is no news to colour, and
 * tinting an unchanged number green would be the app editorialising. That is
 * also why weight, which is always 'none', never gets a colour at all.
 *
 * `inline-block` and not `inline-flex`: as a flex row the arrow is its own flex
 * item, so at 360px a two-line label put the arrow alone on the first line with
 * the words underneath it.
 */
export function DeltaChip({
    verdict,
    delta,
    label,
}: {
    verdict: Verdict;
    delta: number;
    label: string;
}) {
    const flat = verdict === 'flat' || Math.abs(delta) < FLAT;

    const tone =
        verdict === 'better'
            ? 'bg-brand-fill-lime text-brand-fill-ink'
            : verdict === 'worse'
              ? 'bg-brand-fill-orange text-brand-fill-ink'
              : 'text-gray-700';

    return (
        <span className={`inline-block rounded-lg px-2 py-1 text-base font-medium ${tone}`}>
            {!flat && <span aria-hidden="true">{delta > 0 ? '↑ ' : '↓ '}</span>}
            {label}
        </span>
    );
}

/**
 * What the change is called, in words.
 *
 * Weight's verdict is always 'none' by design — the app does not know whether a
 * client is trying to gain, maintain or lose — which is why this has to work
 * without the words "better" and "worse" for at least one metric.
 *
 * `reference` is what the two surfaces disagree about and nothing else. The
 * headline names it — "better than your first report" — because it is the first
 * thing read on the page. A row inside a group has already been given that
 * context by the card around it, so it passes '' and reads "2 points better".
 *
 * Three prepositions, because "better than", "up on" and "same as" are not
 * interchangeable and a client reading "4.5 kg better than" would be being told
 * something the app does not know.
 */
export function changeLabel(metric: TrendMetric, change: TrendChange, reference = ''): string {
    const delta = change.delta as number;

    if (Math.abs(delta) < FLAT) return reference ? `Same as ${reference}` : 'No change';

    const magnitude = `${Math.abs(delta).toFixed(decimalsFor(metric))} ${unitNoun(
        metric,
        Math.abs(delta),
    )}`;

    if (change.verdict === 'better') {
        return reference ? `${magnitude} better than ${reference}` : `${magnitude} better`;
    }
    if (change.verdict === 'worse') {
        return reference ? `${magnitude} worse than ${reference}` : `${magnitude} worse`;
    }

    const way = delta > 0 ? 'up' : 'down';
    return reference ? `${magnitude} ${way} on ${reference}` : `${magnitude} ${way}`;
}

function decimalsFor(metric: TrendMetric): 0 | 1 {
    return metric.key === 'weight' ? 1 : 0;
}

/**
 * What a change of this metric is measured in.
 *
 * The counts are counted in the thing itself — "2 feelings fewer" reads, "2
 * points fewer" does not, because nobody scored anything.
 */
function unitNoun(metric: TrendMetric, magnitude: number): string {
    if (metric.key === 'weight') return 'kg';
    if (metric.key === 'difficultFeelings' || metric.key === 'positiveFeelings') {
        return magnitude === 1 ? 'feeling' : 'feelings';
    }
    if (metric.key === 'physicalSymptoms') {
        return magnitude === 1 ? 'symptom' : 'symptoms';
    }
    return magnitude === 1 ? 'point' : 'points';
}

/** The symptom count in the headline, which is a sentence rather than a metric. */
export function symptomChipLabel(delta: number, verdict: Verdict): string {
    if (verdict === 'flat' || Math.abs(delta) < FLAT) return 'Same as your first report';

    const count = Math.abs(delta);
    const noun = count === 1 ? 'symptom' : 'symptoms';
    return delta < 0
        ? `${count} ${noun} fewer than your first report`
        : `${count} ${noun} more than your first report`;
}
